import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import type { OrganizationsRepository } from '../../domain/interfaces/organizations.interface';
import { OrgContext } from '../../domain/models/organization.model';
import { OrgWheel, OrgWheelAnswers } from '../../domain/models/org-wheel.model';
import {
  OrgEngagement,
  OrgEngagementOverview,
  OrgProgressOp,
  OrgProgressResult,
  OrgPulse,
} from '../../domain/models/org-engagement.model';
import { OrgHealthCatalog, OrgHealthSample, OrgHealthSyncResult } from '../../domain/models/org-health.model';
import {
  OrgBookingResult,
  OrgCounselingOptions,
  OrgCounselingSession,
  OrgGuardianDecisionResult,
  OrgGuardianRequest,
} from '../../domain/models/org-counseling.model';
import { OrgChild, OrgChildDigest, OrgConsentDecision, OrgConsentRequest } from '../../domain/models/org-guardian.model';
import { OrganizationsApiClient } from '../api/organizations.api-client';
import {
  OrgCancelBookingRequestDto,
  OrgHealthSampleRequestDto,
  OrgProgressRequestDto,
} from '../api/dtos/requests';
import {
  toBookingResult,
  toChildDigest,
  toChildren,
  toConsentDecision,
  toConsents,
  toCounselingOptions,
  toCounselingSessions,
  toGuardianDecisionResult,
  toGuardianRequests,
  toHealthCatalog,
  toHealthSyncResult,
  toOrgContext,
  toOrgEngagement,
  toOrgEngagementOverview,
  toOrgProgressResult,
  toOrgPulse,
  toOrgWheel,
} from '../mappers/organizations.mapper';

/**
 * OrganizationsHttpRepository — the REAL binding for every endpoint in
 * `B2B-ORGANIZATIONS-FE-INTEGRATION.md`. Pure transport + mapping: it calls
 * {@link OrganizationsApiClient}, runs the response through the mapper, and hands
 * a DOMAIN value back. No state, no storage, no swallowed errors.
 *
 * Nothing here catches. Every B2B refusal carries meaning the member needs to see
 * — a 403 on a challenge means "not yours", a `409 slot_full` is a race the UI
 * retries past, a 422 carries the server's own ready-to-render Arabic sentence —
 * so failures propagate to the facades, which map them through `OrgErrorMapper`
 * and decide the wording. Catching here would flatten all of that into "something
 * went wrong".
 *
 * `import type` on the repository contract: the providers file imports THIS
 * module as a value to pick an implementation, so a value import back would close
 * a runtime cycle. `implements` + `import type` is erased at compile time, so the
 * contract is still enforced with no runtime edge (same rule as
 * `SubscriptionHttpRepository`).
 */
@Service()
export class OrganizationsHttpRepository implements OrganizationsRepository {
  private readonly api = inject(OrganizationsApiClient);

  // ── §2 context ──────────────────────────────────────────────────────────
  getContext(): Observable<OrgContext> {
    return this.api.getContext().pipe(map(toOrgContext));
  }

  // ── §3 the wheel ────────────────────────────────────────────────────────
  getWheel(): Observable<OrgWheel> {
    return this.api.getWheel().pipe(map(toOrgWheel));
  }

  /**
   * The caller has already filtered `answers` to keys from the wheel it rendered
   * (trap 7), so this sends the map as given — the check-in returns the ENTIRE
   * wheel, which the store re-renders from directly.
   */
  submitCheckin(answers: OrgWheelAnswers): Observable<OrgWheel> {
    return this.api.submitCheckin({ answers }).pipe(map(toOrgWheel));
  }

  // ── §4 engagement ───────────────────────────────────────────────────────
  getEngagement(): Observable<OrgEngagement> {
    return this.api.getEngagement().pipe(map(toOrgEngagement));
  }

  /** Answers with the PARTIAL overview shape (trap 8) — typed so it cannot be misused. */
  joinChallenge(challengeId: number): Observable<OrgEngagementOverview> {
    return this.api.joinChallenge(challengeId).pipe(map(toOrgEngagementOverview));
  }

  /**
   * `date` and `op` are OMITTED rather than sent as null when they carry the
   * server's own defaults (today / `set`), so the wire body matches §4.4 exactly.
   */
  reportProgress(
    challengeId: number,
    value: number,
    op: OrgProgressOp,
    date?: string,
  ): Observable<OrgProgressResult> {
    const body: OrgProgressRequestDto = { value };
    if (date) body.date = date;
    if (op) body.op = op;
    return this.api.reportProgress(challengeId, body).pipe(map(toOrgProgressResult));
  }

  /** Returns the PULSE BLOCK ONLY — merged field-wise by the store, never assigned whole. */
  answerPulse(value: number): Observable<OrgPulse> {
    return this.api.answerPulse({ value }).pipe(map(toOrgPulse));
  }

  // ── §5 health samples ───────────────────────────────────────────────────
  getHealthCatalog(): Observable<OrgHealthCatalog> {
    return this.api.getHealthCatalog().pipe(map(toHealthCatalog));
  }

  syncHealthSamples(samples: readonly OrgHealthSample[]): Observable<OrgHealthSyncResult> {
    const rows: OrgHealthSampleRequestDto[] = samples.map((s) => {
      const row: OrgHealthSampleRequestDto = { metric: s.metric, date: s.date, value: s.value };
      // Both are nullable server-side and default sensibly (`manual` / absent), so
      // an unset value is omitted rather than sent as an explicit null.
      if (s.source) row.source = s.source;
      if (s.recordedAt) row.recorded_at = s.recordedAt;
      return row;
    });
    return this.api.syncHealthSamples({ samples: rows }).pipe(map(toHealthSyncResult));
  }

  // ── §7 school counseling ────────────────────────────────────────────────
  getCounselingOptions(): Observable<OrgCounselingOptions> {
    return this.api.getCounselingOptions().pipe(map(toCounselingOptions));
  }

  bookCounseling(slotId: number, date: string): Observable<OrgBookingResult> {
    return this.api.bookCounseling({ slot_id: slotId, date }).pipe(map(toBookingResult));
  }

  getCounselingSessions(): Observable<readonly OrgCounselingSession[]> {
    return this.api.getCounselingSessions().pipe(map(toCounselingSessions));
  }

  /** The body is optional; an empty object leaves `reason` absent rather than null. */
  cancelCounseling(sessionId: number, reason?: string | null): Observable<readonly OrgCounselingSession[]> {
    const trimmed = reason?.trim();
    const body: OrgCancelBookingRequestDto = trimmed ? { reason: trimmed } : {};
    return this.api.cancelCounseling(sessionId, body).pipe(map(toCounselingSessions));
  }

  // ── §8 guardian ─────────────────────────────────────────────────────────
  getChildren(): Observable<readonly OrgChild[]> {
    return this.api.getChildren().pipe(map(toChildren));
  }

  /** `memberId` is an ORG MEMBER id from `/org/children` (trap 14). */
  getChildDigest(memberId: number): Observable<OrgChildDigest> {
    return this.api.getChildDigest(memberId).pipe(map((res) => toChildDigest(res?.digest)));
  }

  getConsents(): Observable<readonly OrgConsentRequest[]> {
    return this.api.getConsents().pipe(map(toConsents));
  }

  decideConsent(referralId: number, granted: boolean, note?: string | null): Observable<OrgConsentDecision> {
    const trimmed = note?.trim();
    return this.api
      .decideConsent(referralId, trimmed ? { granted, note: trimmed } : { granted })
      .pipe(map(toConsentDecision));
  }

  getGuardianRequests(): Observable<readonly OrgGuardianRequest[]> {
    return this.api.getGuardianRequests().pipe(map(toGuardianRequests));
  }

  decideGuardianRequest(sessionId: number, approve: boolean): Observable<OrgGuardianDecisionResult> {
    return this.api.decideGuardianRequest(sessionId, { approve }).pipe(map(toGuardianDecisionResult));
  }
}
